import React, { useEffect, useState } from "react";
import "./lessons.scss";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { BiArrowBack } from "react-icons/bi";
import { FaTimes, FaUpload } from "react-icons/fa";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import HelperService from "../../Services/HelperService";

const AddLevels = () => {
  const { levelId, lessonId } = useParams();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const [loading, setLoading] = useState(false);
  const [loader, setShowLoader] = useState(false);
  const [error, setError] = useState(false);
  const [isChecked, setIsChecked] = useState("N");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [overview, setOverView] = useState("");
  const [objective, setObjective] = useState("");
  const [fileCategory, setFileCategory] = useState(""); // ✅ renamed for clarity
  const [sponsorImage, setSponsorImage] = useState<File | null>(null);
  const [sponsorImagePreview, setSponsorImagePreview] = useState("");
  const [removeThumb, setRemoveThumb] = useState(false);
  const [levelData, setLevelData] = useState<any>(null);
  const history = useNavigate();

  // 🔹 Fetch Lesson Details if Editing
  useEffect(() => {
    if (lessonId) userdetails();
    levleDetails();
  }, []);

  const userdetails = () => {
    setLoading(true);
    WebService.getAPI({
      action: `/lessons/${lessonId}`,
      body: null,
    })
      .then((res: any) => {
        const result = res.result;
        setLoading(false);

        setValue("name", result.name);
        setValue("overview", result.overview);
        setValue("objective", result.objective);
        setOverView(result.overview);
        setObjective(result.objective);
        setIsChecked(result.is_free ?? "N");
        setSponsorImagePreview(result.thumb_image);
        // ✅ Detect file type if existing file
        if (result.file) {
          setImagePreview(result.file);
          const lowerFile = result.file.toLowerCase();
          if (lowerFile.match(/\.(jpg|jpeg|png|gif|webp)$/))
            setFileCategory("IMAGE");
          else if (lowerFile.match(/\.(mp4|mov|avi|mkv|webm)$/))
            setFileCategory("VIDEO");
          else setFileCategory("FILE");
        }

        // ✅ Split duration (HH:MM)
        if (result.duration) {
          const [hours, minutes] = result.duration.split(":");
          setValue("duration_hours", parseInt(hours));
          setValue("duration_minutes", parseInt(minutes));
        }
      })
      .catch((e: any) => {
        toast.error(e?.response?.data?.message);
        setLoading(false);
      });
  };
  const levleDetails = () => {
    // alert(levelId)
    setLoading(true);
    WebService.getAPI({
      action: `/courses/level/${levelId}`,
      body: null,
    })
      .then((res: any) => {
        const result = res.result;
        setLevelData(result);
        setLoading(false);

        setValue("uuid", result.uuid);
      })
      .catch((e: any) => {
        toast.error(e?.response?.data?.message);
        setLoading(false);
      });
  };

  const handleSponsorImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const selected = e.target.files[0];
      setSponsorImage(selected);
      setSponsorImagePreview(URL.createObjectURL(selected));
      setRemoveThumb(false);
    }
  };

  // 🔹 Remove the selected/existing thumbnail (cleared on Update)
  const removeThumbnail = () => {
    setSponsorImage(null);
    setSponsorImagePreview("");
    setRemoveThumb(true);
  };
  // 🔹 File Selection
  const handleImageSelect = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileURL = URL.createObjectURL(file);
    setImage(file);
    setImagePreview(fileURL);

    // ✅ Detect file type
    const mime = file.type.toLowerCase();
    if (mime.startsWith("image/")) setFileCategory("IMAGE");
    else if (mime.startsWith("video/")) setFileCategory("VIDEO");
    else setFileCategory("FILE");
  };

  // 🔹 Upload File
  const uploadImage = async (file: File, uuid: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("uuid", uuid);

    // ✅ Detect file type cleanly
    const mime = file.type.toLowerCase();
    let detectedType = "FILE";
    if (mime.startsWith("image/")) detectedType = "IMAGE";
    else if (mime.startsWith("video/")) detectedType = "VIDEO";

    form.append("type", detectedType);
    console.log("type", detectedType);
    try {
      await WebService.fileUploadAPI({
        action: "/lesson/image/upload",
        body: form,
      });
      history(-1);
    } catch (error) {
      toast.error("Error uploading file. Please try again.");
      console.error(error);
    }
  };

  // 🔹 Remove Image Preview
  const removeImage = () => {
    setImage(null);
    setImagePreview("");
    setFileCategory("");
  };
  const uploadThumbnailImage = async (file: File, uuid: string) => {
    const form = new FormData();
    form.append("thumb_image", file);
    form.append("uuid", uuid);

    try {
      await WebService.fileUploadAPI({
        action: "/lesson/thumb_image/upload",
        body: form,
      });
      history(-1);
    } catch (error) {
      toast.error("Error uploading thumbnail. Please try again.");
      console.error(error);
    }
  };
  // 🔹 Save / Update Lesson
  const onSave = (data: any) => {
    const totalMinutes =
      parseInt(data.duration_hours || 0) * 60 +
      parseInt(data.duration_minutes || 0);

    const durationFormatted = `${Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0")}:${(totalMinutes % 60).toString().padStart(2, "0")}`;

    const payload = {
      uuid: lessonId,
      name: data.name,
      overview: overview,
      objective: objective,
      is_free: isChecked ?? "N",
      price: null,
      duration: durationFormatted,
      course_level_id: levelId,
      remove_thumb_image: removeThumb ? "Y" : "N",
    };

    setShowLoader(true);

    const apiCall = lessonId
      ? WebService.putAPI({ action: `/lessons/edit`, body: payload })
      : WebService.postAPI({ action: `/lessons/add`, body: payload });

    apiCall
      .then((res: any) => {
        toast.success(
          res.message ||
            (lessonId
              ? "Lesson updated successfully"
              : "Lesson added successfully"),
        );
        setShowLoader(false);

        const Uid = res.result.uuid;
        var goBack = true;
        if (sponsorImage) {
          uploadThumbnailImage(sponsorImage, res.result.uuid);
          goBack = false;
        }
        if (image) {
          uploadImage(image, Uid);
          goBack = false;
        }
        if (goBack) {
          history(-1);
        }
      })
      .catch((e: any) => {
        const message = e?.response?.data?.message;
        toast.error(message || "Something went wrong");
        setError(true);
        setShowLoader(false);
      });
  };

  // 🔹 JSX
  return (
    <Container className="mt-5">
      <Row className="align-items-center mb-4">
        <Col xs="auto">
          <Link
            to=""
            className="d-flex align-items-center text-decoration-none"
          >
            <BiArrowBack
              size={22}
              className="btn btn-link text-decoration-none text-dark d-flex align-items-center p-0 me-2"
              onClick={() => history(-1)}
            />
            <span className="btn btn-link text-decoration-none text-dark d-flex align-items-center p-0">
              Back
            </span>
          </Link>
        </Col>
        <Col className="text-center">
          <h1 className="font-20 font-bold m-0">
            {lessonId ? "Update Lesson" : "Add Lesson"}
          </h1>
        </Col>
        <Col xs="auto"></Col>
      </Row>

      <Row className="justify-content-center">
        <Col lg={12}>
          <Loader show={loading} />
          <Card className="p-4 shadow-sm">
            <Form onSubmit={handleSubmit(onSave)} className="course-form p-4">
              {/* Lesson Name */}
              <Row className="mb-3">
                <Col lg={12}>
                  <Form.Group controlId="lessonname">
                    <Form.Label column sm={3}>
                      Lesson Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter lesson name"
                      {...register("name", { required: true })}
                    />
                  </Form.Group>
                  {errors.name && (
                    <small className="text-danger">
                      Please enter lesson name
                    </small>
                  )}
                </Col>
              </Row>

              {/* Objective */}
              <Row className="mb-3">
                <Col lg={12}>
                  <Form.Group controlId="lessonObjective">
                    <Form.Label column sm={3}>
                      Lesson Objective
                    </Form.Label>
                    <RichTextEditor
                      height="250px"
                      value={objective}
                      onChange={setObjective}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Overview */}
              <Row className="mb-3 mt-5">
                <Col lg={12}>
                  <Form.Group controlId="lessonOverview">
                    <Form.Label column sm={3}>
                      Lesson Overview
                    </Form.Label>
                    <RichTextEditor
                      height="250px"
                      value={overview}
                      onChange={setOverView}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Duration */}
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group controlId="duration" className="mt-3">
                    <Form.Label column sm={3}>
                      Duration 
                    </Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="number"
                        min={0}
                        placeholder="Hours"
                        className="me-2"
                        {...register("duration_hours", { required: true })}
                      />
                      <span className="me-2">hr</span>
                      <Form.Control
                        type="number"
                        min={0}
                        max={59}
                        placeholder="Minutes"
                        {...register("duration_minutes", { required: true })}
                      />
                      <span className="ms-2">min</span>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Paid / Free Toggle */}
              <Row className="mt-5 mb-4">
                <Col lg={6}>
                  <div className="d-flex align-items-center">
                   <Form.Check
  type="checkbox"
  label="Free"
  checked={isChecked === "Y"}
  onChange={(e) =>
    setIsChecked(e.target.checked ? "Y" : "N")
  }
/>

                  </div>
                </Col>
              </Row>

              {/* Pricing */}
              {/* {isChecked === "Y" && (
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group controlId="pricing" className="mt-3">
                      <Form.Label>
                        Pricing <span className="text-muted">(in EUR)</span>
                      </Form.Label>

                      <InputGroup>
                        <InputGroup.Text>€</InputGroup.Text>

                        <Form.Control
                          type="number"
                          placeholder="Enter price"
          
                          {...register("price", {
                            required:
                              isChecked === "Y" ? "Please enter price" : false,
                            validate: (value) =>
                              isChecked === "Y" &&
                              (!value || Number(value) <= 0)
                                ? "Price must be greater than 0"
                                : true,
                          })}
                        />
                      </InputGroup>

                      {errors.price && (
                        <small className="text-danger">
                          {errors.price.message?.toString()}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )} */}
              {/* Thumbnail Image */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group controlId="thumbFile" className="text-start">
                    <Form.Label>Thumbnail Image</Form.Label>
                    <InputGroup>
                      <Form.Control placeholder="Select Thumbnail" readOnly />
                      <label
                        className="upload-material-icon"
                        style={{ cursor: "pointer" }}
                      >
                        <FaUpload />
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleSponsorImageSelect}
                          className="d-none"
                        />
                      </label>
                    </InputGroup>
                    {sponsorImagePreview && (
                      <div className="mt-2 position-relative d-inline-block">
                        <button
                          type="button"
                          onClick={removeThumbnail}
                          title="Remove thumbnail"
                          className="btn btn-danger p-0 d-flex align-items-center justify-content-center"
                          style={{
                            position: "absolute",
                            top: -8,
                            left: -8,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            zIndex: 2,
                          }}
                        >
                          <FaTimes size={12} />
                        </button>
                        <img
                          src={sponsorImagePreview}
                          alt="Thumbnail Preview"
                          height={100}
                          width={100}
                          className="img-thumbnail"
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              {/* File Upload */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group controlId="image" className="text-start">
                    <Form.Label>Lesson File</Form.Label>
                    <InputGroup>
                      <Form.Control placeholder="Select Lesson File" readOnly />
                      <label
                        className="upload-material-icon"
                        style={{ cursor: "pointer" }}
                      >
                        <FaUpload />
                        <Form.Control
                          type="file"
                          accept="image/*,video/*,.pdf,.doc,.docx"
                          onChange={handleImageSelect}
                          className="d-none"
                        />
                      </label>
                    </InputGroup>

                    {/* File Preview */}
                    {imagePreview && (
                      <div className="mt-2 position-relative d-inline-block">
                        {fileCategory === "IMAGE" && (
                          <img
                            src={imagePreview}
                            alt="Lesson File"
                            height={100}
                            width={100}
                            className="img-thumbnail"
                          />
                        )}

                        {fileCategory === "VIDEO" && (
                          <video
                            src={imagePreview}
                            controls
                            height={100}
                            width={150}
                            className="img-thumbnail"
                          />
                        )}

                        {fileCategory === "FILE" && (
                          <div
                            className="d-flex align-items-center justify-content-center border rounded p-3 bg-light"
                           
                          >
                            <a
                              href={imagePreview}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-center"
                            >
                              📄 View File
                            </a>
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={removeImage}
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          style={{
                            top: "-8px",
                            right: "-8px",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          }}
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              {levelData?.is_stage_checker ? (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="QuestionCount" className="mt-3">
                        <Form.Label>Minimum Question Count</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="Minimum Question Count"
                            className="me-2"
                            {...register("stage_checker_min_question_count", {
                              required: false,
                            })}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                ""
              )}
              {/* Submit Button */}
              <Button
                type="submit"
                className="btn btn-brand-1 d-block mx-auto mt-3"
                disabled={loader}
              >
                {loader && <Spinner size="sm" className="me-2" />}
                {lessonId ? "Update" : "Save"}
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AddLevels;
